import {CommonModule} from '@angular/common';
import {Component, EventEmitter, Input, Output} from '@angular/core';
import {RouterModule} from '@angular/router';
import {MetricItem} from '@gatewee/types/system';
import {GateweeIconComponent} from '../gatewee-icon/gatewee-icon.component';

@Component({
  selector: 'gatewee-metric',
  standalone: true,
  imports: [CommonModule, RouterModule, GateweeIconComponent],
  templateUrl: './gatewee-metric.component.html',
  styleUrls: ['./gatewee-metric.component.css'],
})
export class GateweeMetricComponent {
  /** List of metrics; one card per item. */
  @Input() metrics: MetricItem[] = [];

  /** When true, show skeleton cards instead of content. */
  @Input() loading = false;

  /** When true, cards get hover shadow and transition. */
  @Input() hoverable = true;

  /** Number of columns on large screens (e.g. 3 or 4), or 'auto' for auto-fit. */
  @Input() columns: number | 'auto' = 'auto';

  /** Emitted when a card is clicked (with or without link). */
  @Output() metricClick = new EventEmitter<MetricItem>();

  readonly skeletonCount = 4;

  get skeletonItems(): number[] {
    return Array.from({length: this.skeletonCount}, (_, i) => i);
  }

  get gridClass(): string {
    if (this.columns === 'auto') {
      return 'gw-grid gw-grid-cols-1 sm:gw-grid-cols-2 lg:gw-grid-cols-4 gw-gap-5';
    }
    const cols = Math.min(Math.max(this.columns, 1), 6);
    return `gw-grid gw-grid-cols-1 sm:gw-grid-cols-2 lg:gw-grid-cols-${cols} gw-gap-5`;
  }

  getCardClass(metric: MetricItem): string {
    const base =
      'gw-relative gw-bg-background-elevation-2 gw-rounded-2xl gw-p-6 gw-transition-all gw-duration-200';
    const border =
      metric.cardTone === 'negative'
        ? 'gw-border gw-border-status-negative'
        : 'gw-border-base';
    const hover = this.hoverable ? ' hover:gw-shadow-lg gw-cursor-pointer' : '';
    return `${base} ${border}${hover}`;
  }

  getValueToneClass(metric: MetricItem): string {
    return metric.valueTone === 'negative'
      ? 'gw-text-status-negative'
      : 'gw-text-text-primary';
  }

  getDescriptionToneClass(metric: MetricItem): string {
    return metric.descriptionTone === 'negative'
      ? 'gw-text-text-negative'
      : 'gw-text-text-secondary';
  }

  getColorClass(color?: MetricItem['color']): string {
    switch (color) {
      case 'primary':
        return 'gw-text-text-action';
      case 'success':
        return 'gw-text-status-positive';
      case 'warning':
        return 'gw-text-status-warning';
      case 'negative':
        return 'gw-text-status-negative';
      case 'neutral':
      default:
        return 'gw-text-text-secondary';
    }
  }

  onCardClick(metric: MetricItem, event: Event): void {
    this.metricClick.emit(metric);
    if (metric.link && metric.routerLink) {
      event.preventDefault();
    }
  }
}
